import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { brl, pct as pctFmt, fmtDate, fmtDataPrevista, num, stageLabel } from "@/lib/format";
import { trancarPagina } from "@/lib/lockScroll";
import { X, ArrowRight, Loader2 } from "@/components/icons";

// CORRECAO_EMERGENT_RELATORIOS_GANHOS Item 1 — painel lateral (desktop) /
// bottom-sheet (mobile) que mostra a lista por trás de cada card do
// Relatório Comercial. Modos:
// - "ganho": oportunidades ganhas no período
// - "andamento": oportunidades abertas
// - "todas": todas do período
// - "previsao": oportunidades que compõem a previsão ponderada
// - "atencao": items com alerta (não vem daqui — reaproveita `/atencao`)
export default function OportunidadesPanel({ open, mode, period, onClose, extraItems }) {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroOwner, setFiltroOwner] = useState("");

  useEffect(() => {
    if (!open) return;
    const lib = trancarPagina();
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); lib(); };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    if (mode === "atencao" && Array.isArray(extraItems)) {
      setDeals(extraItems); return;
    }
    setLoading(true);
    api.get(`/relatorios/oportunidades?period=${period}&mode=${mode}`)
      .then((r) => setDeals(r.data?.items || []))
      .catch(() => setDeals([]))
      .finally(() => setLoading(false));
  }, [open, mode, period, extraItems]);

  const owners = useMemo(
    () => Array.from(new Set(deals.map((d) => d.owner_name).filter(Boolean))).sort(),
    [deals],
  );
  const visible = useMemo(
    () => (filtroOwner ? deals.filter((d) => d.owner_name === filtroOwner) : deals),
    [deals, filtroOwner],
  );
  const totalValor = useMemo(
    () => visible.reduce((s, d) => s + (Number(d.valor) || 0), 0),
    [visible],
  );
  const totalPonderado = useMemo(
    () => visible.reduce((s, d) => s + (Number(d.valor) || 0) * (Number(d.prob) || 0) / 100, 0),
    [visible],
  );

  const TITULOS = {
    ganho: "Oportunidades ganhas",
    andamento: "Em andamento",
    todas: "Oportunidades no período",
    previsao: "Previsão ponderada · composição",
    atencao: "Requer atenção",
  };
  if (!open) return null;

  const openDeal = (d) => { navigate(`/deal/${d.id}`); onClose?.(); };
  const modoValor = mode === "previsao";

  const ui = (
    <div
      role="dialog" aria-modal="true"
      data-testid="oportunidades-panel"
      className="fixed inset-0 z-[1200] flex justify-center sm:justify-end items-end sm:items-stretch"
      style={{ height: "100dvh" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-white shadow-2xl flex flex-col op-panel sm:ml-auto"
        style={{
          width: "min(480px, 100vw)",
          height: "min(94dvh, 100dvh)",
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          overflowX: "auto",
          right: 0,
        }}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-black/5">
          <div>
            <h2 className="text-base font-bold text-navy">{TITULOS[mode] || "Oportunidades"}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {num(visible.length)}{" "}{visible.length === 1 ? "oportunidade" : "oportunidades"} · {brl(totalValor)}
              {modoValor && <> · ponderado {brl(totalPonderado)}</>}
            </p>
          </div>
          <button
            onClick={() => onClose?.()} aria-label="Fechar"
            className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
          >
            <X className="w-5 h-5 text-navy" />
          </button>
        </header>

        {owners.length > 1 && (
          <div className="px-5 pt-3">
            <select
              value={filtroOwner} onChange={(e) => setFiltroOwner(e.target.value)}
              data-testid="op-panel-filter-owner"
              className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
            >
              <option value="">Todos os vendedores ({owners.length})</option>
              {owners.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-3 sm:px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Carregando…
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-16">Sem oportunidades para exibir.</div>
          ) : visible.map((d) => (
            <button
              type="button" key={d.id} onClick={() => openDeal(d)}
              data-testid={`op-panel-row-${d.id}`}
              className="w-full text-left rounded-xl border border-black/5 bg-white hover:bg-slate-50 p-3 mb-2 flex items-start gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-navy truncate">{d.cliente_nome || "—"}</div>
                <div className="text-xs text-muted-foreground truncate">{d.titulo || "Oportunidade"}</div>
                <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  {d.owner_name && <span>{d.owner_name}</span>}
                  {mode === "ganho" && d.lost_at ? <span>· Ganho em {fmtDate(d.lost_at)}</span> : null}
                  {mode === "ganho" && d.stage_updated_at && !d.lost_at ? <span>· Ganho em {fmtDate(d.stage_updated_at)}</span> : null}
                  {mode !== "ganho" && d.stage ? <span>· {stageLabel(d.stage)}</span> : null}
                  {d.data_prevista && mode !== "ganho" ? <span>· prev. {fmtDataPrevista(d.data_prevista)}</span> : null}
                  {mode === "atencao" && d.motivo ? <span>· {d.motivo}</span> : null}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold text-navy">{brl(d.valor)}</div>
                {modoValor && (
                  <div className="text-[11px] text-slate-500">
                    {pctFmt(d.prob || 0, 0)} · {brl((d.valor || 0) * (d.prob || 0) / 100)}
                  </div>
                )}
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 mt-1 shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
  if (typeof document === "undefined") return ui;
  return createPortal(ui, document.body);
}
